import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import './CounselorVerification.css';

const CounselorVerification = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    credentials: '',
    yearsExperience: '',
    specialization: '',
    universityName: '',
    universityStatus: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    references: [
      { name: '', title: '', organization: '', email: '', phone: '', relationship: '' }
    ]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: user.name || user.email?.split('@')[0] || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, { name: '', title: '', organization: '', email: '', phone: '', relationship: '' }]
    }));
  };

  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['credentials', 'yearsExperience', 'universityName', 'universityStatus', 'phone'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate phone number format
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = await getAccessTokenSilently();
      
      // Submit verification request
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/counselor/submit-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          references: formData.references.filter(ref => ref.name && ref.email) // Only include filled references
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/pending-approval');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit verification request');
      }
    } catch (error) {
      console.error('Verification submission error:', error);
      setError('Failed to submit verification request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="counselor-verification">
        <div className="verification-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Verification Submitted Successfully!</h2>
            <p>Thank you for submitting your counselor verification request.</p>
            <p>Our team will review your credentials and get back to you within 2-3 business days.</p>
            <p>You will receive an email notification once your verification is complete.</p>
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Redirecting to status page...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="counselor-verification">
      <div className="verification-container">
        <div className="verification-header">
          <div className="logo-container">
            <img 
              src="/[NEW]Quila_Logo.png" 
              alt="Quila Logo" 
              className="quila-logo"
            />
          </div>
          <h1>Counselor Verification</h1>
          <p>Please provide your professional credentials for verification. This process typically takes 2-3 business days.</p>
        </div>

        <form onSubmit={handleSubmit} className="verification-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
              </svg>
              Personal Information
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  disabled
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
              </svg>
              Professional Background
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="credentials">Credentials *</label>
                <select
                  id="credentials"
                  name="credentials"
                  value={formData.credentials}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select your credentials</option>
                  <option value="licensed">Licensed Professional Counselor</option>
                  <option value="certified">Certified School Counselor</option>
                  <option value="trainee">Counselor in Training</option>
                  <option value="freelance">Freelance Counselor</option>
                  <option value="other">Other Professional Credential</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="yearsExperience">Years of Experience *</label>
                <input
                  type="number"
                  id="yearsExperience"
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleInputChange}
                  min="0"
                  max="50"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="specialization">Areas of Specialization</label>
              <textarea
                id="specialization"
                name="specialization"
                value={formData.specialization}
                onChange={handleInputChange}
                placeholder="e.g., College admissions, Career counseling, Academic planning, Test preparation"
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor"/>
              </svg>
              University Information
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="universityName">University Name *</label>
                <input
                  type="text"
                  id="universityName"
                  name="universityName"
                  value={formData.universityName}
                  onChange={handleInputChange}
                  placeholder="e.g., University of California, Berkeley"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="universityStatus">Current Status *</label>
                <select
                  id="universityStatus"
                  name="universityStatus"
                  value={formData.universityStatus}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select your status</option>
                  <option value="currently_attending">Currently Attending</option>
                  <option value="degree_obtained">Degree Obtained</option>
                  <option value="not_attending">Not Currently Attending</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" fill="currentColor"/>
              </svg>
              Address Information
            </h3>
            <div className="form-group">
              <label htmlFor="streetAddress">Street Address</label>
              <input
                type="text"
                id="streetAddress"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleInputChange}
                placeholder="123 Education Street"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Springfield"
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="IL"
                  maxLength="2"
                />
              </div>
              <div className="form-group">
                <label htmlFor="zipCode">ZIP Code</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="62701"
                  maxLength="10"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4C16 5.11 15.11 6 14 6C12.89 6 12 5.11 12 4C12 2.89 12.89 2 14 2C15.11 2 16 2.89 16 4M20 22V16H22V22H20M22 13H20V11H22V13M6 6C7.11 6 8 5.11 8 4C8 2.89 7.11 2 6 2C4.89 2 4 2.89 4 4C4 5.11 4.89 6 6 6M7 22V16H9V22H7M9 13H7V11H9V13M6 8C3.79 8 2 9.79 2 12H4C4 10.9 4.9 10 6 10C7.1 10 8 10.9 8 12C8 13.1 7.1 14 6 14M18 8C15.79 8 14 9.79 14 12H16C16 10.9 16.9 10 18 10C19.1 10 20 10.9 20 12C20 13.1 19.1 14 18 14Z" fill="currentColor"/>
              </svg>
              Professional References
            </h3>
            <p className="section-description">Please provide at least one professional reference who can verify your credentials.</p>
            
            {formData.references.map((reference, index) => (
              <div key={index} className="reference-item">
                <div className="reference-header">
                  <h4>Reference {index + 1}</h4>
                  {formData.references.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeReference(index)}
                      className="remove-reference-btn"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`ref-name-${index}`}>Name</label>
                    <input
                      type="text"
                      id={`ref-name-${index}`}
                      value={reference.name}
                      onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                      placeholder="Reference Name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`ref-title-${index}`}>Title</label>
                    <input
                      type="text"
                      id={`ref-title-${index}`}
                      value={reference.title}
                      onChange={(e) => handleReferenceChange(index, 'title', e.target.value)}
                      placeholder="e.g., Principal, Director"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`ref-org-${index}`}>Organization</label>
                    <input
                      type="text"
                      id={`ref-org-${index}`}
                      value={reference.organization}
                      onChange={(e) => handleReferenceChange(index, 'organization', e.target.value)}
                      placeholder="e.g., Springfield High School"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`ref-email-${index}`}>Email</label>
                    <input
                      type="email"
                      id={`ref-email-${index}`}
                      value={reference.email}
                      onChange={(e) => handleReferenceChange(index, 'email', e.target.value)}
                      placeholder="reference@school.edu"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`ref-phone-${index}`}>Phone</label>
                    <input
                      type="tel"
                      id={`ref-phone-${index}`}
                      value={reference.phone}
                      onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`ref-relationship-${index}`}>Relationship</label>
                    <input
                      type="text"
                      id={`ref-relationship-${index}`}
                      value={reference.relationship}
                      onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                      placeholder="e.g., Supervisor, Colleague"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addReference}
              className="add-reference-btn"
            >
              Add Another Reference
            </button>
          </div>

          <div className="form-section">
            <div className="consent-checkbox">
              <input
                type="checkbox"
                id="consent"
                required
              />
              <label htmlFor="consent">
                I consent to Quila contacting my references to verify my credentials and professional experience.
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="back-btn"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Verification Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CounselorVerification; 